import { useQuery } from '@tanstack/react-query';
import httpClient from '../api/httpClient';
import { API_ENDPOINTS } from '../api/endpoints';
import { PaginatedResponse } from '../api/types';
import { Instrument } from '../api/types';

interface PriceData {
    symbol: string;
    bid: number;
    ask: number;
    last: number;
    provider: string;
    timestamp: number;
}

interface UseAllPricesReturn {
    prices: Map<string, PriceData>;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

// Fetch prices from instruments endpoint (which includes price data)
// Uses per_page=1000 to get all instruments without pagination
const fetchAllPrices = async (): Promise<Map<string, PriceData>> => {
    try {
        const response = await httpClient.get<PaginatedResponse<Instrument>>(
            API_ENDPOINTS.TRADER.INSTRUMENTS,
            {
                params: {
                    per_page: 1000, // Get all instruments in one request
                    page: 1,
                },
            }
        );

        const priceMap = new Map<string, PriceData>();

        if (response.data && response.data.data) {
            response.data.data.forEach((instrument: Instrument) => {
                // Extract price data from instrument.price object (as returned by backend)
                const priceData = (instrument as any).price;

                if (priceData) {
                    const bid = priceData.bid;
                    const ask = priceData.ask;
                    const last = priceData.last ?? instrument.current_price ?? 0;

                    // Only add price if bid/ask are provided
                    if (bid !== null && bid !== undefined && ask !== null && ask !== undefined) {
                        priceMap.set(instrument.id, {
                            symbol: instrument.symbol,
                            bid: Math.max(0, bid), // Ensure bid is never negative
                            ask: ask,
                            last: last,
                            provider: 'backend',
                            timestamp: priceData.timestamp || Date.now(),
                        });
                    }
                }
            });
        }

        return priceMap;
    } catch (error) {
        console.error('Failed to fetch prices from instruments endpoint:', error);
        return new Map();
    }
};

export const useAllPrices = (
    _instrumentIds: string[] = [], // No longer used, kept for backward compatibility
    _pollingInterval?: number // No longer used, kept for backward compatibility
): UseAllPricesReturn => {
    const {
        data: prices = new Map<string, PriceData>(),
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['all-prices-instruments'],
        queryFn: () => fetchAllPrices(),
        // No polling — prices are pushed via WebSocket (usePriceSocket)
        staleTime: Infinity,
        gcTime: 5 * 60 * 1000,
        retry: 3,
        retryDelay: 1000,
    });

    return {
        prices,
        isLoading,
        error: error?.message || null,
        refetch,
    };
};
